/* import __COLOCATED_TEMPLATE__ from './predicates-bridge.hbs'; */
/* RESPONSIBLE TEAM: team-fin-qa */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import { registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import PredicateGroup from 'predicates/models/predicate-group';

interface Signature {
  Args: {};
}

export default class PredicatesBridge extends Component<Signature> {
  @service declare store: $TSFixMe;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare appService: $TSFixMe;

  @tracked showPredicatesEditorSideSheet = false;
  @tracked predicateGroup?: $TSFixMe;
  @tracked finInvolvedPredicates: unknown[] = [];

  constructor(owner: unknown, args: {}) {
    super(owner, args);

    let unsubscribeOpen = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'fin-qa-monitor:predicates:open',
      async (data) => {
        let eventData = data as {
          predicates?: unknown[];
        };

        // Clear predicateGroup to show loading state and open the side sheet immediately
        this.predicateGroup = undefined;
        this.showPredicatesEditorSideSheet = true;

        try {
          await Promise.all([
            this.store.findAll('conversation-attributes/descriptor'),
            this.appService.app.reload(),
            this.store.findAll('ai-agent/guidelines'),
            this.store.findAll('ai-agent/escalation-rule'),
            ...(this.appService.app.currentAdmin.canAccessProductSettings
              ? [this.store.findAll('qa-monitor/cx-score-reason').catch((_e: unknown) => undefined)]
              : []),
            ...(this.appService.app.canUseFeature('ai_topics')
              ? [
                  // Topics/subtopics are optional — continue if endpoints return 404
                  this.store.findAll('qa-monitor/ai-topic').catch(() => undefined),
                  this.store.findAll('qa-monitor/ai-subtopic').catch(() => undefined),
                ]
              : []),
          ]);
        } catch (e: unknown) {
          // Close the side sheet if core data loading fails
          this.showPredicatesEditorSideSheet = false;
          return;
        }

        let nonRoutingGuidelineValues = new Set(
          this.store
            .peekAll('ai-agent/guidelines')
            .filter((g: $TSFixMe) => g.category !== 'routing')
            .map((g: $TSFixMe) => `guideline_${g.id}`),
        );

        let expandGuidanceOnly = (preds: $TSFixMe[]): $TSFixMe[] =>
          preds.map((p: $TSFixMe) => {
            if (p.predicates) {
              return { ...p, predicates: expandGuidanceOnly(p.predicates) };
            }
            if (
              p.attribute === 'conversation.guidance_applied' &&
              typeof p.value === 'string' &&
              p.value.startsWith('guideline_') &&
              nonRoutingGuidelineValues.has(p.value)
            ) {
              return { ...p, attribute: 'conversation.guidance_only' };
            }
            return p;
          });

        let allPredicates: unknown[] = expandGuidanceOnly(eventData.predicates || []);

        this.finInvolvedPredicates = allPredicates.filter(
          (p: $TSFixMe) => p.attribute === 'conversation.fin_involved',
        );
        let editablePredicates = allPredicates.filter(
          (p: $TSFixMe) => p.attribute !== 'conversation.fin_involved',
        );

        this.predicateGroup = this.store.createFragment('predicates/predicate-group', {
          predicates: PredicateGroup.convertRawPredicates(this.store, editablePredicates),
        });
      },
    );

    let unsubscribeClose = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'fin-qa-monitor:predicates:close',
      () => {
        this.showPredicatesEditorSideSheet = false;
      },
    );

    registerDestructor(this, () => {
      unsubscribeOpen();
      unsubscribeClose();
    });
  }

  @action closePredicatesEditorSideSheet() {
    this.showPredicatesEditorSideSheet = false;
    this.reactTeammateAppIsland.dispatchBridgeEvent('teammate-app:event', {
      identifier: 'fin-qa-monitor:predicates:closed',
      data: {},
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'FinQaMonitor::PredicatesBridge': typeof PredicatesBridge;
    'fin-qa-monitor/predicates-bridge': typeof PredicatesBridge;
  }
}
